/* ═══════════════════════════════════════════════════════
   CoreFlow Call Logger
   Logs completed calls to Supabase call_logs + activities
   ═══════════════════════════════════════════════════════ */

import { supabase } from "./supabase";

/**
 * Log a completed call to Supabase
 * @param {Object} params
 * @param {string} params.contactId - UUID of the contact called
 * @param {string} params.dealId - Optional UUID of the related deal
 * @param {string} params.userId - UUID of the calling user
 * @param {string} params.orgId - UUID of the organization
 * @param {string} params.phoneNumber - The number dialed (or From number for inbound)
 * @param {number} params.duration - Call duration in seconds
 * @param {string} params.status - completed|missed|voicemail|no_answer|busy
 * @param {string} params.outcome - Optional outcome/disposition text
 * @param {string} params.notes - Optional call notes
 * @param {string} params.twilioSid - Optional Twilio Call SID
 * @param {string} params.direction - "outbound" (default) or "inbound"
 */
export async function logCall({
  contactId,
  dealId,
  userId,
  orgId,
  phoneNumber,
  duration = 0,
  status = "completed",
  outcome = "",
  notes = "",
  twilioSid = null,
  direction = "outbound",
}) {
  try {
    // 1. Insert into call_logs
    const callLog = {
      org_id: orgId,
      user_id: userId,
      contact_id: contactId || null,
      deal_id: dealId || null,
      direction: direction,
      status: status,
      outcome: outcome || (status === "completed" ? "Connected" : status === "no_answer" ? "No answer" : status),
      duration: duration,
      twilio_sid: twilioSid,
      notes: phoneNumber || notes,
      started_at: new Date(Date.now() - duration * 1000).toISOString(),
      ended_at: new Date().toISOString(),
    };

    const { data: logData, error: logError } = await supabase
      .from("call_logs")
      .insert(callLog)
      .select()
      .single();

    if (logError) {
      console.error("[CoreFlow] call_logs insert error:", logError.message);
      // Defense in depth: don't drop the call data when the INSERT
      // itself fails (RLS, network, schema, etc.). The outer catch
      // only sees thrown exceptions — Supabase returns errors as
      // values, so without this we'd silently lose the row.
      queueForRetry({ contactId, dealId, userId, orgId, phoneNumber, duration, status, outcome, notes, twilioSid, direction });
    } else {
      console.log("[CoreFlow] Call logged:", logData.id);
    }

    // 2. Insert into activities for the timeline
    const activity = {
      org_id: orgId,
      user_id: userId,
      contact_id: contactId || null,
      deal_id: dealId || null,
      type: "call",
      title: formatCallDescription(status, duration, phoneNumber, outcome),
      metadata: {
        phone_number: phoneNumber,
        duration: duration,
        status: status,
        outcome: outcome,
        twilio_sid: twilioSid,
        call_log_id: logData?.id || null,
      },
    };

    const { error: actError } = await supabase.from("activities").insert(activity);

    if (actError) {
      console.error("[CoreFlow] activity insert error:", actError.message);
    }

    // 3. Update contact's last_activity_at
    if (contactId) {
      await supabase
        .from("contacts")
        .update({ last_activity_at: new Date().toISOString() })
        .eq("id", contactId);
    }

    return { success: !logError, callLogId: logData?.id || null, error: logError?.message || null };
  } catch (err) {
    console.error("[CoreFlow] logCall failed:", err.message);
    // Queue for retry — don't lose call data
    queueForRetry({ contactId, dealId, userId, orgId, phoneNumber, duration, status, outcome, notes, twilioSid, direction });
    return { success: false, error: err.message };
  }
}

// ── Retry queue for failed call logs ──
const RETRY_KEY = "cf_failed_call_logs";
function queueForRetry(params) {
  try {
    const queue = JSON.parse(localStorage.getItem(RETRY_KEY) || "[]");
    queue.push({ ...params, failedAt: new Date().toISOString() });
    localStorage.setItem(RETRY_KEY, JSON.stringify(queue.slice(-50))); // Keep max 50
    console.warn("[CoreFlow] Call log queued for retry. Queue size:", queue.length);
  } catch (e) { /* localStorage full or unavailable */ }
}

export async function retryFailedLogs() {
  try {
    const queue = JSON.parse(localStorage.getItem(RETRY_KEY) || "[]");
    if (queue.length === 0) return;
    console.log("[CoreFlow] Retrying", queue.length, "failed call logs...");
    const remaining = [];
    for (const params of queue) {
      const result = await logCall(params);
      if (!result.success) remaining.push(params);
    }
    localStorage.setItem(RETRY_KEY, JSON.stringify(remaining));
    if (remaining.length === 0) console.log("[CoreFlow] ✅ All failed logs retried successfully");
    else console.warn("[CoreFlow] Still", remaining.length, "failed logs in queue");
  } catch (e) { /* ignore */ }
}

function formatCallDescription(status, duration, phoneNumber, outcome) {
  const mins = Math.floor(duration / 60);
  const secs = duration % 60;
  const durStr = duration > 0 ? ` (${mins}:${String(secs).padStart(2, "0")})` : "";

  switch (status) {
    case "completed":
      return `Outbound call to ${phoneNumber}${durStr}${outcome ? " — " + outcome : ""}`;
    case "no_answer":
      return `Called ${phoneNumber} — no answer`;
    case "voicemail":
      return `Called ${phoneNumber} — left voicemail`;
    case "busy":
      return `Called ${phoneNumber} — busy`;
    case "missed":
      return `Missed call from ${phoneNumber}`;
    default:
      return `Call to ${phoneNumber}${durStr}`;
  }
}

/**
 * Get call history for a contact
 */
export async function getCallHistory(contactId, limit = 20) {
  const { data, error } = await supabase
    .from("call_logs")
    .select("*, users(full_name)")
    .eq("contact_id", contactId)
    .order("started_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("[CoreFlow] getCallHistory error:", error.message);
    return [];
  }
  return data;
}

/**
 * Get call stats for dashboard
 */
export async function getCallStats(orgId, days = 7) {
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from("call_logs")
    .select("status, duration")
    .eq("org_id", orgId)
    .gte("started_at", since);

  if (error) return { total: 0, connected: 0, avgDuration: 0, connectRate: 0 };

  const total = data.length;
  const connected = data.filter((c) => c.status === "completed" && c.duration > 0).length;
  const totalDuration = data.reduce((sum, c) => sum + (c.duration || 0), 0);

  return {
    total,
    connected,
    avgDuration: connected > 0 ? Math.round(totalDuration / connected) : 0,
    connectRate: total > 0 ? Math.round((connected / total) * 100) : 0,
  };
}
